import { useState } from 'react';

import ContextMenu from 'dashboard/components/ContextMenu';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import Permissioned from 'dashboard/components/Permissioned';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import TableLayout, {
  TableActionTD,
  TableLinkTR,
  TableNullState,
  TableTD,
  TableTH,
  TableTR,
} from 'dashboard/layouts/TableLayout';
import { Team } from 'dashboard/models/team';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { Span } from 'design-system/components/Text';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { Permission } from 'lib/permissions';

export interface TeamsListProps {
  teams: Team[];
  isLoading: boolean;
  showCreateTeamModal: () => void;
  deleteTeam: (teamId: string) => Promise<void>;
}

export const TeamsList = ({
  teams,
  isLoading,
  showCreateTeamModal,
  deleteTeam,
}: TeamsListProps) => {
  const [deleteTeamModal, { enable: showDeleteTeamModal, disable: hideDeleteTeamModal }] =
    useToggle(false);
  const [currentTeamId, setCurrentTeamId] = useState('');
  const dropdownOptions = [
    {
      id: 'delete-team',
      label: 'Delete',
      onClick: showDeleteTeamModal,
      hidden: !usePermission(Permission.TeamWrite),
      variant: 'danger' as const,
    },
  ];

  return (
    <>
      <Card>
        <Card.Body>
          <Card.Title>Teams</Card.Title>
          <TableLayout
            dataTest='teams-table'
            isLoading={isLoading}
            showHeader={true}
            isEmpty={teams.length === 0}
            nullState={
              <TableNullState dataTestBlock='teams-table__null-state'>
                <Permissioned
                  permission={Permission.TeamWrite}
                  fallback={<Span>There are no teams in your organization.</Span>}
                >
                  <Span>Create a team to get started.</Span>
                  <Spacer height={2} />
                  <Button
                    id='users-table__create-team-button'
                    onClick={showCreateTeamModal}
                    width='button'
                    variant='basic'
                  >
                    Create team
                  </Button>
                </Permissioned>
              </TableNullState>
            }
            headerTR={
              <>
                <TableTH id='case-templates-table__name-header'>Name</TableTH>
                <TableTH id='case-templates-table__users-header'>Users</TableTH>
                <TableTH>{/* Actions column */}</TableTH>
              </>
            }
          >
            {teams.map((team) => (
              <Permissioned
                key={team.id}
                permission={Permission.TeamView}
                fallback={
                  <TableTR>
                    <TableTD>
                      <Span>{team.attributes.name}</Span>
                    </TableTD>
                    <TableTD>
                      <Span>{team.attributes['active-user-count']}</Span>
                    </TableTD>
                    <TableActionTD>
                      {dropdownOptions.length > 0 && (
                        <ContextMenu
                          items={dropdownOptions}
                          renderItem={(item) => (
                            <ContextMenu.Item
                              key={item.id}
                              id={`teams-list__${item.id}--${team.id}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                setCurrentTeamId(team.id);
                                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                                item.onClick();
                              }}
                              variant={item.variant}
                            >
                              {item.label}
                            </ContextMenu.Item>
                          )}
                        />
                      )}
                    </TableActionTD>
                  </TableTR>
                }
              >
                <TableLinkTR to={`/dashboard/teams/${team.id}`}>
                  <TableTD>
                    <Span>{team.attributes.name}</Span>
                  </TableTD>
                  <TableTD>
                    <Span>{team.attributes['active-user-count']}</Span>
                  </TableTD>
                  <TableActionTD>
                    {dropdownOptions.length > 0 && (
                      <ContextMenu
                        items={dropdownOptions}
                        renderItem={(item) => (
                          <ContextMenu.Item
                            key={item.id}
                            id={`teams-list__${item.id}--${team.id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              setCurrentTeamId(team.id);
                              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                              item.onClick();
                            }}
                            variant={item.variant}
                          >
                            {item.label}
                          </ContextMenu.Item>
                        )}
                      />
                    )}
                  </TableActionTD>
                </TableLinkTR>
              </Permissioned>
            ))}
          </TableLayout>
        </Card.Body>
      </Card>
      <ModalWrapper isOpen={deleteTeamModal} handleCloseModal={hideDeleteTeamModal}>
        <ConfirmationModal
          cancelAction={hideDeleteTeamModal}
          confirmAction={() => deleteTeam(currentTeamId)}
          confirmActionText='Delete'
        >
          Are you sure you want to delete this team? This action is irreversable.
        </ConfirmationModal>
      </ModalWrapper>
    </>
  );
};

export default TeamsList;
